export interface CrashDiagnostic {
  id: string;
  title: string;
  reason: string;
  advice: string;
  actionHref?: string;
  actionLabel?: string;
}

interface PatternRule {
  id: string;
  pattern: RegExp;
  title: string;
  reason: string;
  advice: string;
  actionHref?: (serverId: string) => string;
  actionLabel?: string;
}

export const CRASH_RULES: PatternRule[] = [
  {
    id: "java_version",
    pattern:
      /UnsupportedClassVersionError|has been compiled by a more recent version of the Java Runtime/i,
    title: "Несовместимая версия Java",
    reason: "Сервер или установленные плагины скомпилированы под более новую версию Java.",
    advice: "Перейдите во вкладку «Настройки» и переключите версию Java (например, на Java 21).",
    actionHref: (id) => `#/servers/${encodeURIComponent(id)}/settings`,
    actionLabel: "В Настройки",
  },
  {
    id: "oom",
    pattern: /java\.lang\.OutOfMemoryError|insufficient memory for the Java Runtime Environment/i,
    title: "Нехватка оперативной памяти (OOM)",
    reason: "Процесс Minecraft исчерпал выделенную память RAM.",
    advice:
      "Уменьшите view-distance и simulation-distance в server.properties или оптимизируйте моды/плагины.",
    actionHref: (id) => `#/servers/${encodeURIComponent(id)}/files`,
    actionLabel: "Открыть Файлы",
  },
  {
    id: "port_bind",
    pattern: /FAILED TO BIND TO PORT|Address already in use/i,
    title: "Порт сервера ещё занят",
    reason: "Предыдущий контейнер или процесс ещё не успел освободить сетевой сокет.",
    advice: "Подождите 10–15 секунд и попробуйте запустить сервер снова.",
  },
  {
    id: "session_lock",
    pattern: /Failed to check session lock|session\.lock/i,
    title: "Мир заблокирован (session.lock)",
    reason: "Файл session.lock остался от предыдущего аварийного завершения сервера.",
    advice: "Убедитесь, что сервер остановлен, и удалите файл world/session.lock.",
    actionHref: (id) => `#/servers/${encodeURIComponent(id)}/files`,
    actionLabel: "Открыть Файлы",
  },
  {
    id: "eula",
    pattern: /You need to agree to the EULA in order to run the server/i,
    title: "Лицензия EULA не принята",
    reason: "Minecraft требует подтверждения согласия с условиями EULA.",
    advice: "Откройте eula.txt в файловом менеджере и установите значение eula=true.",
    actionHref: (id) => `#/servers/${encodeURIComponent(id)}/files`,
    actionLabel: "Открыть eula.txt",
  },
  {
    id: "mod_missing_deps",
    pattern:
      /Missing or unsupported mandatory dependencies|ModResolutionException|requires .+ which is missing/i,
    title: "Отсутствуют зависимости модов",
    reason: "Один или несколько модов требуют стороннюю библиотеку для запуска.",
    advice:
      "Проверьте зависимости установленных модов (например, наличие Fabric API в папке mods).",
    actionHref: (id) => `#/servers/${encodeURIComponent(id)}/files`,
    actionLabel: "Проверить папку mods",
  },
  {
    id: "plugin_fail",
    pattern: /Could not load 'plugins\/|Error occurred while enabling .+ \(Is it up to date\?\)/i,
    title: "Ошибка запуска плагина",
    reason: "Плагин не смог инициализироваться на выбранной версии ядра.",
    advice:
      "Проверьте совместимость версий плагина в папке plugins или удалите устаревший .jar файл.",
    actionHref: (id) => `#/servers/${encodeURIComponent(id)}/plugins`,
    actionLabel: "В Плагины",
  },
];

export function detectCrashFromLogs(lines: string[], serverId?: string): CrashDiagnostic | null {
  if (!lines || lines.length === 0) {
    return null;
  }
  // Проверяем последние 80 строк лога от новых к старым
  const recent = lines.slice(-80).reverse();
  for (const line of recent) {
    for (const rule of CRASH_RULES) {
      if (rule.pattern.test(line)) {
        return {
          id: rule.id,
          title: rule.title,
          reason: rule.reason,
          advice: rule.advice,
          actionHref: serverId && rule.actionHref ? rule.actionHref(serverId) : undefined,
          actionLabel: rule.actionLabel,
        };
      }
    }
  }
  return null;
}
